import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  Timestamp,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Channel, Token } from '@capacitor/push-notifications';
import { auth, db } from './firebase';
import app from './firebase';
import { recordReadWriteCount, withReadCounter } from '../utils/withReadCounter';

export type NotificationKind =
  | 'follow' | 'like' | 'comment' | 'reply' | 'mention' | 'message'
  | 'report_update' | 'broadcast' | 'emergency' | 'account_suspension';

export interface AppNotification {
  id: string;
  type: NotificationKind;
  actorUid: string;
  actorName: string;
  actorAvatar: string;
  targetType: string;
  targetId: string;
  preview: string;
  createdAt: string;
  groupKey: string;
  count: number;
  lastActors: Array<{ uid: string; name: string; avatar: string }>;
  unread: boolean;
}

export interface NotificationSettings {
  pushEnabled: boolean;
  follows: boolean;
  likes: boolean;
  comments: boolean;
  mentions: boolean;
  broadcasts: boolean;
  messages: boolean;
  quietHours: { start: string; end: string; tz: string };
}

export interface NotificationCounters {
  unreadNotifications: number;
  lastReadAt: Timestamp | null;
}

export const defaultNotificationSettings: NotificationSettings = {
  pushEnabled: false,
  follows: true,
  likes: true,
  comments: true,
  mentions: true,
  broadcasts: true,
  messages: true,
  quietHours: { start: '', end: '', tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' }
};

export interface NotificationPage {
  items: AppNotification[];
  cursor: QueryDocumentSnapshot | null;
  hasMore: boolean;
}

const countersRef = (uid: string) => doc(db, 'users', uid, 'meta', 'counters');
const notificationCollection = (uid: string) => collection(db, 'users', uid, 'notifications');

export const dispatchNotificationOptInPrompt = (): void => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('yetrykar:notification-opt-in'));
};

export const subscribeToNotificationCounters = (
  uid: string,
  callback: (counters: NotificationCounters) => void
): (() => void) => onSnapshot(countersRef(uid), snapshot => {
  recordReadWriteCount('notification-badge', { reads: snapshot.metadata.fromCache ? 0 : 1 });
  callback({
    unreadNotifications: Math.max(0, Number(snapshot.get('unreadNotifications')) || 0),
    lastReadAt: (snapshot.get('lastReadAt') as Timestamp | undefined) ?? null
  });
}, error => {
  console.error('Could not subscribe to notification counters:', error);
});

export const loadNotificationPage = async (
  uid: string,
  cursor: QueryDocumentSnapshot | null = null,
  lastReadAt: Timestamp | null = null
): Promise<NotificationPage> => {
  const constraints = [
    orderBy('createdAt', 'desc'),
    ...(cursor ? [startAfter(cursor)] : []),
    limit(20)
  ];
  const snapshot = await withReadCounter(
    'notifications',
    () => getDocs(query(notificationCollection(uid), ...constraints)),
    result => ({ reads: result.metadata.fromCache ? 0 : result.size })
  );
  const docs = snapshot.docs;
  return {
    items: docs.map(item => {
      const data = item.data();
      const createdAt = data.createdAt as Timestamp | undefined;
      const actor = isRecord(data.actor) ? data.actor : null;
      return {
        id: item.id,
        type: data.type as NotificationKind,
        actorUid: String(data.actorUid || ''),
        actorName: String(data.actorName || actor?.name || 'YeTryKar user'),
        actorAvatar: String(data.actorAvatar || actor?.avatar || ''),
        targetType: String(data.targetType || ''),
        targetId: String(data.targetId || ''),
        preview: String(data.preview || ''),
        createdAt: createdAt?.toDate().toISOString() || new Date().toISOString(),
        groupKey: String(data.groupKey || ''),
        count: Math.max(1, Number(data.count) || 1),
        lastActors: Array.isArray(data.lastActors) ? data.lastActors.slice(0, 3) : [],
        unread: !!createdAt && (!lastReadAt || createdAt.toMillis() > lastReadAt.toMillis())
      };
    }),
    cursor: docs.at(-1) ?? cursor,
    hasMore: snapshot.docs.length === 20
  };
};

export const markAllNotificationsRead = async (uid: string): Promise<void> => {
  if (auth.currentUser?.uid !== uid) throw new Error('You can only update your own notification counter.');
  await setDoc(countersRef(uid), {
    lastReadAt: serverTimestamp(),
    unreadNotifications: 0
  }, { merge: true });
  recordReadWriteCount('notification-badge', { writes: 1 });
};

export const loadNotificationSettings = async (uid: string): Promise<NotificationSettings> => {
  const snapshot = await withReadCounter(
    'notification-settings',
    () => getDoc(doc(db, 'users', uid, 'settings', 'notifications')),
    result => ({ reads: result.metadata.fromCache ? 0 : 1 })
  );
  const saved = snapshot.data() ?? {};
  const quietHours = isRecord(saved.quietHours) ? saved.quietHours : {};
  return {
    pushEnabled: saved.pushEnabled === true,
    follows: saved.follows !== false,
    likes: saved.likes !== false,
    comments: saved.comments !== false,
    mentions: saved.mentions !== false,
    broadcasts: saved.broadcasts !== false,
    messages: saved.messages !== false,
    quietHours: {
      start: typeof quietHours.start === 'string' ? quietHours.start : '',
      end: typeof quietHours.end === 'string' ? quietHours.end : '',
      tz: typeof quietHours.tz === 'string' ? quietHours.tz : defaultNotificationSettings.quietHours.tz
    }
  };
};

export const saveNotificationSettings = async (
  uid: string,
  settings: NotificationSettings
): Promise<void> => {
  if (auth.currentUser?.uid !== uid) throw new Error('You can only update your own notification settings.');
  await setDoc(doc(db, 'users', uid, 'settings', 'notifications'), settings, { merge: false });
  recordReadWriteCount('notification-settings', { writes: 1 });
};

const getDeviceId = (uid: string): string => {
  const key = `yetrykar_push_device_${uid}`;
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
};

const saveDeviceToken = async (uid: string, token: string, platform: 'web' | 'android') => {
  await setDoc(doc(db, 'users', uid, 'devices', getDeviceId(uid)), {
    token,
    platform,
    lastActive: serverTimestamp(),
    createdAt: serverTimestamp()
  }, { merge: true });
  recordReadWriteCount('notification-devices', { writes: 1 });
};

let nativeListenersReady = false;
let webListenerReady = false;

const setupNativePushListeners = async () => {
  if (nativeListenersReady) return;
  await PushNotifications.addListener('registration', (token: Token) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    void saveDeviceToken(uid, token.value, 'android').catch(error => {
      console.error('Could not save Android push token:', error);
    });
  });
  await PushNotifications.addListener('registrationError', error => {
    console.error('Android push registration failed:', error);
  });
  await PushNotifications.addListener('pushNotificationReceived', notification => {
    window.dispatchEvent(new CustomEvent('yetrykar:push-received', { detail: notification }));
  });
  nativeListenersReady = true;
};

export const enablePushNotifications = async (uid: string): Promise<void> => {
  if (auth.currentUser?.uid !== uid) throw new Error('Sign in before enabling notifications.');
  if (Capacitor.isNativePlatform()) {
    let permission = await PushNotifications.checkPermissions();
    if (permission.receive !== 'granted') permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') {
      throw new Error('Notifications are disabled. Enable them in Android Settings > Apps > YeTryKar > Notifications.');
    }
    await Promise.all(([
      ['social', 'Social', 'Follows, likes and comments'],
      ['broadcast', 'Broadcasts', 'Important community updates'],
      ['emergency', 'Emergency', 'Urgent safety announcements']
    ] as const).map(([id, name, description]) => PushNotifications.createChannel({
      id, name, description, importance: id === 'emergency' ? 5 : 3, visibility: 1
    } as Channel)));
    await setupNativePushListeners();
    await PushNotifications.register();
    return;
  }

  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    throw new Error('Push notifications are not supported in this browser.');
  }
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapidKey) throw new Error('Set VITE_FIREBASE_VAPID_KEY to enable web push.');
  let permission = Notification.permission;
  if (permission === 'default') permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notifications are blocked. Enable them in your browser site settings.');
  }
  if (!await isSupported()) throw new Error('Firebase web messaging is not supported in this browser.');
  const workerUrl = new URL('/firebase-messaging-sw.js', window.location.origin);
  workerUrl.searchParams.set('config', JSON.stringify(app.options));
  const serviceWorker = await navigator.serviceWorker.register(`${workerUrl.pathname}${workerUrl.search}`);
  const token = await getToken(getMessaging(app), { vapidKey, serviceWorkerRegistration: serviceWorker });
  if (!token) throw new Error('Firebase did not return a web push token.');
  await saveDeviceToken(uid, token, 'web');
  if (!webListenerReady) {
    onMessage(getMessaging(app), payload => {
      window.dispatchEvent(new CustomEvent('yetrykar:push-received', {
        detail: {
          title: payload.notification?.title || 'YeTryKar',
          body: payload.notification?.body || 'You have a new notification.'
        }
      }));
    });
    webListenerReady = true;
  }
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export const sendLiveNotificationToFollowers = async (
  hostUid: string,
  hostName: string,
  isLiveEnding = false,
  streamTitle = ''
): Promise<void> => {
  if (!hostUid) return;
  try {
    const followersSnap = await getDocs(collection(db, 'users', hostUid, 'followers'));
    const followerUids = followersSnap.docs.map(d => d.data().uid || d.id).filter(Boolean);
    const notificationPromises = followerUids.map(async followerUid => {
      const notifRef = doc(collection(db, 'users', followerUid, 'notifications'));
      await setDoc(notifRef, {
        type: 'broadcast',
        actorUid: hostUid,
        actorName: hostName,
        preview: isLiveEnding
          ? `${hostName} has ended their live stream.`
          : `🔴 ${hostName} is NOW LIVE: "${streamTitle || 'Live Stream'}"`,
        createdAt: serverTimestamp(),
        unread: true
      });
    });
    await Promise.all(notificationPromises);
  } catch (err) {
    console.warn('Could not send live stream notification to followers:', err);
  }
};
